/**
 * Automation Block Component (Doc-View)
 *
 * Renders the automation block in normal document view (inside a note).
 */

import { I18n } from '@affine/i18n';
import {
  blockPendingDeletionSource,
  blockPendingDeletionState,
  subscribeBlockPendingDeletion,
} from '@blocksuite/affine-model';
import {
  ArrowRightBigIcon,
  DatabaseTableViewIcon,
  FilterIcon,
  PlayFillIcon,
} from '@blocksuite/icons/lit';
import { BlockComponent } from '@blocksuite/std';
import { html } from 'lit';

import type { AutomationBlockModel } from '../schemas/index.js';
import {
  type BlockPendingDeletionText,
  renderBlockPendingDeletionNotice,
} from '../trashcan/block-pending-deletion-notice.js';
import { applyPageModeFilter } from '../utils/page-mode-filter.js';
import * as styles from './automation-block.css.js';
import { resolveAutomationName } from './automation-name.js';

export const AFFINE_AUTOMATION = 'affine-automation';

const PENDING_DELETION_TEXT = {
  queued: 'com.streamlain.dataview.database.deletionPending.queued',
  confirming: 'com.streamlain.dataview.database.deletionPending.confirming',
  unconfirmed: 'com.streamlain.dataview.database.deletionPending.unconfirmed',
  cancel: 'com.streamlain.dataview.database.deletionPending.cancel',
  retry: 'com.streamlain.dataview.database.deletionPending.retry',
} as const satisfies Record<BlockPendingDeletionText, string>;

export class AutomationBlockComponent extends BlockComponent<AutomationBlockModel> {
  override connectedCallback() {
    super.connectedCallback();
    // A queued trash shows its phase until the server confirms it.
    this.disposables.add(
      subscribeBlockPendingDeletion(this.model, () => this.requestUpdate())
    );

    // E018-SPK021: Hide canvas automations in page mode
    if (!applyPageModeFilter(this)) return;
  }

  override renderBlock() {
    const model = this.model;
    const workflowName = (model.props as Record<string, unknown>)
      .workflowName as string | null;
    const title = resolveAutomationName(
      {
        title: model.props.title,
        titleMode: model.props.titleMode,
        workflowName,
      },
      I18n.t('com.streamlain.streamlain.common.new-automation')
    );
    const { sourceTableId, workflowId, outputMode } = model.props;
    const { filterCount, mappingCount } = model;

    return html`
      <div class=${styles.automationBlock}>
        ${this.renderDeletionPendingNotice()}
        <div class=${styles.header}>
          <span class=${styles.icon}>${PlayFillIcon()}</span>
          <span class=${styles.name}>${title}</span>
        </div>

        <div class=${styles.summary}>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${DatabaseTableViewIcon()}</span>
            <span>
              ${sourceTableId
                ? `Source table (${filterCount} filters, ${mappingCount} mappings)`
                : 'No source configured'}
            </span>
          </div>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${FilterIcon()}</span>
            <span>${workflowId ? 'Workflow configured' : 'No workflow'}</span>
          </div>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${ArrowRightBigIcon()}</span>
            <span>${this._formatOutputMode(outputMode)}</span>
          </div>
        </div>
      </div>
    `;
  }

  /** The line of a queued trash, the same on page and canvas. */
  protected renderDeletionPendingNotice() {
    const source = blockPendingDeletionSource(this.model);
    const run = (action: Promise<unknown> | undefined) => {
      action?.catch(error => {
        console.error('[AutomationBlock] Pending trash action failed:', error);
      });
    };
    return renderBlockPendingDeletionNotice(
      blockPendingDeletionState(this.model),
      {
        kind: 'automation',
        text: key => I18n.t(PENDING_DELETION_TEXT[key]),
        classes: {
          notice: styles.pendingDeletion,
          message: styles.pendingDeletionMessage,
          button: styles.pendingDeletionAction,
        },
        onCancel: () => run(source?.cancel(this.store.id, this.model.id)),
        onRetry: () => run(source?.retry(this.store.id, this.model.id)),
      }
    );
  }

  private _formatOutputMode(mode: string): string {
    switch (mode) {
      case 'update_source':
        return 'Update source table';
      case 'create_table':
        return 'Create new table';
      case 'append':
        return 'Append to target';
      case 'update_target':
        return 'Update target table';
      default:
        return mode;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_AUTOMATION]: AutomationBlockComponent;
  }
}
